import axios from 'axios';
import { getServerErrorMessage } from './errors';

export const apiUrl = () => window.location.hostname === 'localhost'
  ? 'http://localhost:5013'
  : window.location.origin;

export const assetBaseUrl = () => {
  const host = window.location.hostname;
  if (host.includes('dev') || host === 'localhost' || host === '127.0.0.1') {
    return 'https://assets.dev.ontik.io/';
  }
  if (host.includes('staging')) {
    return 'https://assets.staging.ontik.io/';
  }
  return 'https://assets.ontik.io/';
};

// Create an Axios instance
const api = axios.create({ baseURL: apiUrl() });

const REFRESH_THRESHOLD_MS = 300000; // treat a token within 5 minutes of expiry as needing a refresh

// The current auth/tenant context, attached to every outgoing request by the single request
// interceptor below. Holding these as plain values (rather than ejecting/re-adding interceptors on
// every token refresh) means there is no window in which a request can go out with a stale token or a
// missing X-Merchant-Id — the interceptor always reads the latest value at dispatch time.
let currentToken = null;
let currentTokenExpiry = null;
let currentMerchantId = null;
let currentApiVersion = null;

// Registered by the auth layer so the request/response handlers below can mint a fresh access token,
// instead of surfacing an "expired token" error to the UI. Concurrent refreshes share a single
// in-flight promise so we don't fire N silent-token requests at once.
let tokenRefreshHandler = null;
let inflightTokenRefresh = null;

export const setTokenRefreshHandler = (handler) => {
  tokenRefreshHandler = handler;
};

export const refreshAuthToken = () => {
  if (!tokenRefreshHandler) {
    return Promise.reject(new Error('No token refresh handler registered'));
  }
  if (!inflightTokenRefresh) {
    inflightTokenRefresh = Promise.resolve()
      .then(() => tokenRefreshHandler())
      .finally(() => {
        inflightTokenRefresh = null;
      });
  }
  return inflightTokenRefresh;
};

const tokenNeedsRefresh = () =>
  !currentToken ||
  (currentTokenExpiry !== null && currentTokenExpiry - Date.now() < REFRESH_THRESHOLD_MS);

api.interceptors.response.use(response => {
  return response;
}, error => {

  if (error.response) {
    const { status } = error.response;
    if (status === 426) {
      // Redirect to custom 404 page
      window.location.reload();
    }
  }

  return Promise.reject(error);
});

api.interceptors.response.use(response => {
  return response;
}, error => {

  if (error.config && error.config.bypassInterceptor) {
    return Promise.reject(error);
  }

  if (error.response) {
    const { status, data, config } = error.response;
    if (status === 403) {
      // Check if this is an SSO enforcement error
      const message = getServerErrorMessage(error);
      if (message.includes('Single Sign-On') || message.includes('email/password')) {
        // SSO enforcement - show error and force logout
        alert(message + '\n\nYou will now be logged out.');
        window.location.href = '/logout';
        return Promise.reject(error);
      }
      // Other forbidden errors - continue normal flow
    } else if (status === 404) {
      // Redirect to custom 404 page
      window.location.href = '/not-found';
    } else if (status === 500) {
      const errorMessage = getServerErrorMessage(error) || error.message || 'Server Error';
      const newRelicError = new Error(errorMessage);

      // Attach additional context for debugging (New Relic isn't loaded in dev — guard it).
      window.newrelic?.noticeError?.(newRelicError, {
        status,
        url: config.url,
        method: config.method,
        responseData: data
      });
    }
  }

  return Promise.reject(error);
});

// Store the current access token (and its expiry, decoded from the JWT) for the request interceptor.
export const setAuthToken = (token) => {
  currentToken = token || null;
  currentTokenExpiry = null;
  if (token) {
    try {
      const { exp } = JSON.parse(atob(token.split('.')[1]));
      currentTokenExpiry = exp * 1000;
    } catch {
      currentTokenExpiry = null;
    }
  }
};

export const ACTIVE_MERCHANT_STORAGE_KEY = 'activeMerchantId';

const readStoredMerchantId = () => {
  try {
    return localStorage.getItem(ACTIVE_MERCHANT_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

// Store the active merchant id sent as X-Merchant-Id on every request. It is also mirrored to
// localStorage so the interceptor can attach the header on requests that fire before the merchant
// context has finished loading (e.g. anything not gated behind the merchant guard).
export const setActiveMerchantIdHeader = (merchantId) => {
  currentMerchantId = merchantId || null;
  try {
    if (currentMerchantId) {
      localStorage.setItem(ACTIVE_MERCHANT_STORAGE_KEY, String(currentMerchantId));
    }
  } catch {
    // localStorage unavailable (private mode / SSR); the in-memory value still applies.
  }
};

// Store the app version sent as X-Api-Version on every request.
export const setApiVersionHeader = (version) => {
  currentApiVersion = version || null;
};

// The single request interceptor: attach the current auth/tenant context to every request, refreshing
// the token first if it is missing or near expiry. Because it reads the latest values at dispatch time
// (rather than being swapped out on each refresh), no request can slip out mid-swap without a token or
// merchant header — which was the source of intermittent 401/403s on requests fired right as the tab
// regained focus and the token was being silently refreshed.
api.interceptors.request.use(async config => {
  if (tokenRefreshHandler && tokenNeedsRefresh()) {
    try {
      await refreshAuthToken();
    } catch {
      // Fall through: send whatever token we have; the 401 response handler is the final safety net.
    }
  }

  // Fall back to the persisted merchant id so requests that fire before loadMerchants runs (i.e.
  // before setActiveMerchantIdHeader) still carry X-Merchant-Id, rather than 403ing tenant-less.
  const merchantId = currentMerchantId || readStoredMerchantId();

  if (currentToken) config.headers.Authorization = `Bearer ${currentToken}`;
  if (merchantId) config.headers['X-Merchant-Id'] = merchantId;
  if (currentApiVersion) config.headers['X-Api-Version'] = currentApiVersion;

  return config;
});

// Safety net for a 401 that slips past the proactive refresh above (e.g. a token invalidated
// server-side before its expiry): silently refresh and replay the original request once. The replay
// goes back through the request interceptor, which re-attaches the fresh token, so react-query sees
// success rather than an error state. This deliberately runs even for CalDAV's bypassInterceptor
// requests — the flag only opts out of the global redirects above, not out of token recovery.
api.interceptors.response.use(response => response, async error => {
  const original = error.config;

  if (error.response?.status === 401 && original && !original._retryAfterRefresh) {
    original._retryAfterRefresh = true;
    try {
      await refreshAuthToken();
      return api.request(original);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }

  return Promise.reject(error);
});

export default api;



